import { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import RatingStars from './RatingStars';
import reviewService from '../../services/reviewService';
import './ReviewSummary.css';

/**
 * ReviewSummary Component
 *
 * Displays aggregate rating statistics:
 * - Average rating with large star display
 * - Total review count
 * - Rating distribution bars (5★ to 1★)
 * - Verified purchase count
 * - Reviews with photos count
 *
 * Distribution bars are clickable to filter reviews
 */
const ReviewSummary = memo(function ReviewSummary({
  productId,
  onFilterByRating
}) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await reviewService.getReviewSummary(productId);
        setSummary(response.data);
      } catch (err) {
        console.error('Failed to load review summary:', err);
        setError('Failed to load review summary');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchSummary();
    }
  }, [productId]);

  // Calculate percentage for distribution bar
  const getPercentage = (count, total) => {
    if (total === 0) return 0;
    return Math.round((count / total) * 100);
  };

  // Handle distribution bar click
  const handleBarClick = (rating) => {
    if (onFilterByRating) {
      onFilterByRating(rating);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="review-summary review-summary--loading">
        <div className="review-summary__skeleton">
          <div className="skeleton skeleton--box"></div>
          <div className="skeleton skeleton--bars"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="review-summary review-summary--error">
        <p className="review-summary__error-message">
          <i className="fas fa-exclamation-triangle"></i> {error}
        </p>
      </div>
    );
  }

  // Empty state - don't render anything, let ReviewList handle empty messaging
  if (!summary || summary.totalReviews === 0) {
    return null;
  }

  const { averageRating, totalReviews, ratingDistribution, verifiedPurchaseCount, withPhotosCount } = summary;

  return (
    <div className="review-summary">
      <h3 className="review-summary__header">Customer Reviews</h3>

      <div className="review-summary__content">
        {/* Average Rating Box */}
        <div className="review-summary__avg">
          <div className="review-summary__avg-number">
            {averageRating.toFixed(1)}
          </div>
          <RatingStars rating={averageRating} size="lg" />
          <div className="review-summary__avg-text">
            Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
          </div>
        </div>

        {/* Distribution Bars */}
        <div className="review-summary__distribution">
          <h4 className="review-summary__distribution-header">Rating Distribution</h4>
          {[5, 4, 3, 2, 1].map((rating) => {
            const count = ratingDistribution[rating] || 0;
            const percentage = getPercentage(count, totalReviews);

            return (
              <div
                key={rating}
                className="review-summary__bar"
                onClick={() => handleBarClick(rating)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleBarClick(rating);
                  }
                }}
                aria-label={`Filter by ${rating} star reviews (${count} reviews)`}
              >
                <span className="review-summary__bar-label">
                  {rating} ★
                </span>
                <div className="review-summary__bar-track">
                  <div
                    className="review-summary__bar-fill"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <span className="review-summary__bar-count">
                  {count} ({percentage}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Additional Stats */}
      {(verifiedPurchaseCount > 0 || withPhotosCount > 0) && (
        <div className="review-summary__stats">
          {verifiedPurchaseCount > 0 && (
            <span className="review-summary__stat">
              <i className="fas fa-check-circle"></i> {verifiedPurchaseCount} verified purchases
            </span>
          )}
          {withPhotosCount > 0 && (
            <span className="review-summary__stat">
              <i className="fas fa-camera"></i> {withPhotosCount} with photos
            </span>
          )}
        </div>
      )}
    </div>
  );
});

ReviewSummary.propTypes = {
  productId: PropTypes.number.isRequired,
  onFilterByRating: PropTypes.func
};

export default ReviewSummary;
